import {
	State,
	getInitialState,
	setBucketRange,
	setDescription,
	setImportDate,
	setHasTimeSeriesData,
	setHasKeyFiguresData,
	setHasMarketShareData,
	setCompetitorLoading,
	setKeyFiguresLoading,
	setMarketShareLoading
} from './Helpers';
import React, { createContext, useContext, useReducer } from 'react';
import Actions from './Actions';

type Dispatch = (action: any) => void;

interface ProviderProps {
	description: string;
	children: React.ReactNode;
}

interface IndustryInsightsContextType {
	state: State;
	dispatch: Dispatch;
}

const ACTION_HANDLERS: { [key: string]: (s: State, a: any) => State } = {
	[Actions.SET_BUCKET_RANGE]: (state, { bucketRange }) => ({ ...state, bucketRange }),
	[Actions.SET_DESCRIPTION]: (state, { description }) => ({ ...state, description }),
	[Actions.SET_IMPORT_DATE]: (state, { date }) => ({ ...state, importDate: date }),
	[Actions.SET_HAS_TIME_SERIES_DATA]: (state, { has }) => ({ ...state, hasTimeSeriesData: has }),
	[Actions.SET_HAS_KEY_FIGURES_DATA]: (state, { has }) => ({ ...state, hasKeyFiguresData: has }),
	[Actions.SET_HAS_MARKET_SHARE_DATA]: (state, { has }) => ({ ...state, hasMarketShareData: has }),
	[Actions.SET_COMPETITOR_LOADING]: (state, { loading }) => ({ ...state, competitorLoading: loading }),
	[Actions.SET_KEY_FIGURES_LOADING]: (state, { loading }) => ({ ...state, keyFiguresLoading: loading }),
	[Actions.SET_MARKET_SHARE_LOADING]: (state, { loading }) => ({ ...state, marketShareLoading: loading })
};

const reducer = (state: State, action: any) => {
	const handler = ACTION_HANDLERS[action.type];
	return handler ? handler(state, action) : state;
};

const IndustryInsightsContext = createContext<IndustryInsightsContextType | undefined>(undefined);

export function IndustryInsightsProvider({ description, children }: ProviderProps) {
	const [state, dispatch] = useReducer(reducer, getInitialState(description));
	return <IndustryInsightsContext.Provider value={{ state, dispatch }}>{children}</IndustryInsightsContext.Provider>;
}

export function useIndustryInsights() {
	const context = useContext(IndustryInsightsContext);
	if (!context) {
		throw new Error('useIndustryInsights must be used within IndustryInsightsProvider');
	}

	const { state, dispatch } = context;

	const actions = {
		setBucketRange: setBucketRange(dispatch),
		setDescription: setDescription(dispatch, state),
		setImportDate: setImportDate(dispatch),
		setHasTimeSeriesData: setHasTimeSeriesData(dispatch),
		setHasKeyFiguresData: setHasKeyFiguresData(dispatch),
		setHasMarketShareData: setHasMarketShareData(dispatch),
		setCompetitorLoading: setCompetitorLoading(dispatch),
		setKeyFiguresLoading: setKeyFiguresLoading(dispatch),
		setMarketShareLoading: setMarketShareLoading(dispatch)
	};

	return { state, ...actions };
}
